import {
  LEAD_CHANNEL_LABELS,
  LEAD_STATUS_LABELS,
  Lead,
  LeadChannel,
  LeadStatus,
} from '../../types';
import { resolveLeadAlert } from './leadUtils';

export type LeadReportSummary = {
  total: number;
  byStatus: { status: LeadStatus; label: string; count: number }[];
  byOrigin: { origin: string; label: string; count: number }[];
  bySeller: { sellerId: number | null; label: string; count: number }[];
  alertsAttention: number;
  alertsUrgent: number;
  withoutSeller: number;
  daysAvgWithoutContact: number | null;
  won: number;
  lost: number;
  conversionRate: number | null;
};

export const buildLeadReportSummary = (
  leads: Lead[],
  getSellerName?: (id: number | null | undefined) => string
): LeadReportSummary => {
  const byStatusMap = new Map<LeadStatus, number>();
  (Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).forEach((s) => byStatusMap.set(s, 0));

  const byOriginMap = new Map<string, number>();
  const bySellerMap = new Map<string, { sellerId: number | null; label: string; count: number }>();
  let alertsAttention = 0;
  let alertsUrgent = 0;
  let withoutSeller = 0;
  let daysSum = 0;
  let daysCount = 0;

  leads.forEach((lead) => {
    byStatusMap.set(lead.status, (byStatusMap.get(lead.status) ?? 0) + 1);

    const originKey = lead.origin || 'sem_origem';
    byOriginMap.set(originKey, (byOriginMap.get(originKey) ?? 0) + 1);

    const sellerId = lead.seller_id ?? null;
    const sellerKey = sellerId == null ? 'none' : String(sellerId);
    const sellerLabel =
      sellerId == null
        ? 'Sem vendedor'
        : lead.seller?.name || getSellerName?.(sellerId) || `ID ${sellerId}`;
    const prevSeller = bySellerMap.get(sellerKey);
    if (prevSeller) prevSeller.count += 1;
    else bySellerMap.set(sellerKey, { sellerId, label: sellerLabel, count: 1 });

    const alert = resolveLeadAlert(lead);
    if (alert === 'atencao') alertsAttention += 1;
    if (alert === 'urgente') alertsUrgent += 1;
    if (lead.seller_id == null) withoutSeller += 1;

    const ref = lead.last_contact_at || lead.first_contact_at;
    if (ref) {
      const date = new Date(ref.slice(0, 10) + 'T12:00:00');
      if (!Number.isNaN(date.getTime())) {
        const today = new Date();
        today.setHours(12, 0, 0, 0);
        const days = Math.max(0, Math.floor((today.getTime() - date.getTime()) / 86400000));
        daysSum += days;
        daysCount += 1;
      }
    }
  });

  const won = byStatusMap.get('ganho') ?? 0;
  const lost = byStatusMap.get('perdido') ?? 0;
  const closed = won + lost;

  return {
    total: leads.length,
    byStatus: (Object.keys(LEAD_STATUS_LABELS) as LeadStatus[]).map((status) => ({
      status,
      label: LEAD_STATUS_LABELS[status],
      count: byStatusMap.get(status) ?? 0,
    })),
    byOrigin: Array.from(byOriginMap.entries())
      .map(([origin, count]) => ({
        origin,
        label:
          origin === 'sem_origem'
            ? 'Sem origem'
            : LEAD_CHANNEL_LABELS[origin as LeadChannel] ?? origin,
        count,
      }))
      .sort((a, b) => b.count - a.count),
    bySeller: Array.from(bySellerMap.values()).sort((a, b) => b.count - a.count),
    alertsAttention,
    alertsUrgent,
    withoutSeller,
    daysAvgWithoutContact: daysCount ? Math.round((daysSum / daysCount) * 10) / 10 : null,
    won,
    lost,
    conversionRate: closed > 0 ? Math.round((won / closed) * 1000) / 10 : null,
  };
};
